import { useCallback, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { ParticipantPermission } from 'livekit-server-sdk'
import {
  DataPacket_Kind,
  LocalParticipant,
  ParticipantKind,
  RemoteParticipant,
  RoomEvent,
} from 'livekit-client'

import { useMaybeRoomContext } from '@/src/context'

import { ChatToggle } from '@/src/components/controls/ChatToggle'
import { Message } from '@/src/components/room/Chat/components/Message/Message'
import { ChatForm } from '@/src/components/room/Chat/components/ChatForm/ChatForm'

import { ChatCloseIcon } from '@/src/assets/icons'

import { BASE_URL_MEET } from '@/src/constants/env'
import { IChatProps, IMessage, IMessagesGetResponse } from '@/src/types/chat.type'

import { convertTimestampToHHMM } from '@/src/helpers/convertToHHMM'
import { handlePublishData } from '@/src/helpers/handlePublishData'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'
import {
  selectChatState,
  selectLocalParticipantId,
  setChatMessages,
  setLocalParticipantId,
} from '@/src/store/slices/chat'
import { useGroups } from '@/src/hooks/participant/useGroups'

export const Chat = ({ hideCloseBtn, isPiP }: IChatProps) => {
  const { t } = useTranslation()
  const room = useMaybeRoomContext()
  const dispatch = useAppDispatch()
  const { text_primary_color } = useAppSelector(selectBranding)
  const { messages, isOpen } = useAppSelector(selectChatState)
  const localParticipantId = useAppSelector(selectLocalParticipantId)

  const ulRef = useRef<HTMLUListElement | null>(null)

  const { getCurrentUserGroupId } = useGroups()

  const filteredMessages = useMemo(() => {
    return messages.filter((message) => {
      if (getCurrentUserGroupId === null) {
        if (message.groupId === undefined || message.groupId === null) {
          return true
        }
        return false
      }
      
      return message.groupId === getCurrentUserGroupId
    })
  }, [messages, getCurrentUserGroupId])

  useEffect(() => {
    if (ulRef) {
      ulRef.current?.scrollTo({ top: ulRef.current.scrollHeight })
    }
  }, [ulRef, filteredMessages])

  const isLocalMessage = useCallback(
    (message: IMessage) => localParticipantId === message?.author?.id,
    [localParticipantId],
  )

  const showMetaData = useCallback(
    (message: IMessage, prevMessage: IMessage) => {
      const createdAtTitle = convertTimestampToHHMM(message?.created_at)

      let lessThenMinute: boolean = false

      if (prevMessage?.created_at) {
        lessThenMinute = convertTimestampToHHMM(prevMessage?.created_at) === createdAtTitle
      }

      let showMeta: boolean = true

      if (!lessThenMinute) {
        showMeta = true
      } else if (prevMessage?.author?.id === message?.author?.id) {
        showMeta = false
      } else {
        showMeta = true
      }

      return {
        createdAtTitle,
        showMeta,
        isLocalMessage: isLocalMessage(message),
      }
    },
    [isLocalMessage],
  )

  const getMessages = useCallback(async () => {
    const roomId: string | null = await room?.getSid()?.catch((err) => {
      console.error('Error with getting identity of room: \n', err)
      return null
    })

    if (!roomId || typeof roomId !== 'string') return []

    return await axios
      .get<IMessagesGetResponse>(
        `${BASE_URL_MEET}/api/chat/messages?roomName=${room?.name}&roomId=${roomId}`,
      )
      ?.catch((error) => {
        console.error(`Error with getting messages for chat: \n`, error)
      })
      ?.then((res) => {
        if (!res) return []

        const messages = res?.data?.data ?? []
        dispatch(setChatMessages(messages))

        return messages
      })
  }, [room])

  useEffect(() => {
    if (!room || isPiP) return

    const handleRoomConnected = () => {
      setTimeout(async () => await getMessages(), 300)
    }

    const handleDataReceived = async (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic?.includes('chat-topic')) {
        const decoded = JSON.parse(new TextDecoder().decode(payload))

        if (decoded?.updateMessages) {
          try {
            await getMessages()
          } catch (e) {
            console.error('Error with updating messages: \n', e)
          }
        }
      }
    }

    const handlePermissionsChanged = (
      permissions: ParticipantPermission,
      participant: RemoteParticipant | LocalParticipant,
    ) => {
      if (
        participant?.permissions?.canPublish &&
        participant?.permissions?.canSubscribe &&
        participant?.kind !== ParticipantKind.AGENT
      ) {
        handlePublishData(
          room,
          { updateMessages: true },
          {
            reliable: true,
            topic: 'chat-topic',
            destinationIdentities: [participant?.identity],
          },
        )
      }
    }

    room
      ?.on(RoomEvent.Connected, handleRoomConnected)
      ?.on(RoomEvent.DataReceived, handleDataReceived)
      ?.on(RoomEvent.ParticipantPermissionsChanged, handlePermissionsChanged)

    return () => {
      room
        ?.off(RoomEvent.Connected, handleRoomConnected)
        ?.off(RoomEvent.DataReceived, handleDataReceived)
        ?.off(RoomEvent.ParticipantPermissionsChanged, handlePermissionsChanged)
    }
  }, [room, getMessages, isPiP])

  useEffect(() => {
    if (!room?.localParticipant?.identity || isPiP) return

    if (!localParticipantId) {
      dispatch(setLocalParticipantId(room?.localParticipant?.identity))
    }
  }, [room?.localParticipant?.identity, isPiP, localParticipantId])

  return (
    isOpen && (
      <aside className="lk-chat">
        <header className="lk-chat-header">
          <span
            className="lk-chat-header-title"
            style={{ paddingLeft: isPiP ? 0 : 48, color: text_primary_color }}
          >
            {t('chat.title')}
          </span>

          {!hideCloseBtn && (
            <ChatToggle className="lk-close-button" closeBar>
              <ChatCloseIcon />
            </ChatToggle>
          )}
        </header>

        <ul className="lk-list lk-chat-messages chat-messages" ref={ulRef}>
          {filteredMessages?.map((message, index) => {
            const { createdAtTitle, showMeta, isLocalMessage } = showMetaData(
              message,
              index === 0 ? null : filteredMessages?.at(index - 1),
            )

            return (
              <Message
                key={message?.id}
                showMetaData={showMeta}
                isLocalMessage={isLocalMessage}
                createdAtTitle={createdAtTitle}
                {...message}
              />
            )
          })}
        </ul>

        <ChatForm isPiP={isPiP} getMessages={getMessages} />
      </aside>
    )
  )
}
